export const DEFAULT_PAPER_STARTING_COLLATERAL_USD = 10_000;
// Default fee schedule when a campaign's paperTradingConfig omits maker/taker fees.
// 0 bps (fee-free): every configured competition sets 0, so fee-free is the intended
// baseline — a non-zero default silently taxed campaigns that left the field blank.
export const PAPER_MAKER_FEE_BPS = 0;
export const PAPER_TAKER_FEE_BPS = 0;
export const PAPER_MAINTENANCE_MARGIN_RATIO = 0.005;
/** BOOK model: levels of the Hyperliquid l2Book walked before the synthetic ladder. */
export const BOOK_VISIBLE_LEVELS = 5;
/**
 * Synthetic leverage floor: paper margin is simulated internally, so HL's
 * per-asset caps don't bind. Effective per-pair cap = max(HL cap, floor).
 * Applied once, in the API pair-meta publisher — frontends read the
 * effective cap from `GET v1/hub/paper-trading/pair-metas`.
 */
export const PAPER_MAX_LEVERAGE_FLOOR = 40;
export const PAPER_RETENTION_PERIOD_DAYS = 90;
/**
 * How long a pair may go without a price before it stops being tradeable.
 *
 * Derivation, not a hand-picked number. Every listed pair is priced by the
 * per-venue `allMids` heartbeat, so the slowest healthy pair is bounded by that
 * feed's cadence, measured 2026-09-24 over 40 min on both live venues (952
 * frames): median 5.04 s, p99 5.36 s, max 5.60 s, no gap above 10 s, no
 * reconnects. 10 s is therefore ~1.8 worst-observed intervals — it absorbs the
 * measured tail and still trips inside one further beat when a venue stops.
 * Re-derive from a fresh cadence measurement if the venue's interval changes;
 * do not widen it to silence an alert, since a breach is the signal that the
 * feed died.
 */
export const PAPER_STALENESS_THRESHOLD_SECONDS = 10;
export const PAPER_STALENESS_THRESHOLD_MS = PAPER_STALENESS_THRESHOLD_SECONDS * 1000;
export const PAPER_INNER_QUEUE_CONCURRENCY = 1;

/**
 * Per-pair debounce window on the HL book-update stream that drives the
 * resting-LIMIT cross watcher. HL publishes book updates roughly every
 * 100-500 ms per pair; collapsing a burst of frames into a single
 * evaluation cuts DB load with no perceptible latency penalty for fills.
 */
export const PAPER_LIMIT_CROSS_DEBOUNCE_MS = 100;

/**
 * Ceiling on how long the trailing debounce above may defer an evaluation.
 * Without it an unbroken run of sub-window frames re-arms the timer forever
 * and the pair is never evaluated for the length of the run.
 */
export const PAPER_LIMIT_CROSS_DEBOUNCE_MAX_WAIT_MS = 500;

/**
 * Per-pair debounce window on the HL mark stream that drives the TP/SL
 * trigger watcher. Same rationale as the cross watcher above: without it
 * every mark frame costs one `paper_order` scan per subscribed pair, and
 * on a bot-traded pair that scan is thousands of rows.
 *
 * Bounded by the staleness breaker, not by this: a trigger still fires
 * against the mark read at evaluation time, so the added latency is at
 * most one window.
 */
export const PAPER_TRIGGER_DEBOUNCE_MS = 100;

/** Ceiling on the trailing window above, same rationale as the cross watcher. */
export const PAPER_TRIGGER_DEBOUNCE_MAX_WAIT_MS = 500;

/**
 * Minimum age (ms) a resting LIMIT must reach before it is eligible for
 * an HL-cross auto-fill. Acts as a latency tolerance: covers the
 * round-trip between a place-order request landing on the API and the
 * order showing up in `LocalOrderBookCache`. Without this gate, a user
 * placing a limit at the same instant HL ticks across it could be
 * filled by the very same WS frame that arrived after their request
 * was already in flight — a confusing race we'd rather not expose.
 */
export const PAPER_LIMIT_CROSS_MIN_TIME_ON_BOOK_MS = 1_000;
